"use client";
import { track } from "@amplitude/analytics-react-native";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { amp } from "src/api/amplitude";
import { PUBLIC_ALBUM_VIEWABLE } from "src/api/featureFlags";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import JoinGatheringError from "src/api/graphql/__generated__/enums/JoinGatheringError";
import { GatheringInitialEntryFragmentFragment } from "src/api/graphql/__generated__/graphql";
import hasValidGatheringAccess from "src/helpers/gatheringHelpers/hasValidGatheringAccess";
import log from "src/helpers/log";
import useStateRef from "src/hooks/useStateRef";
import { TextInputHandler } from "src/swsh-native";
import toast from "src/swsh-native/toast";
import GatheringPreviewPrivate from "../components/GatheringPreviewPrivate";
import GatheringPreviewPublic from "../components/GatheringPreviewPublic";
import GatheringPublicFull from "../components/GatheringPublicFull";
import useJoinGathering from "../hooks/useJoinGathering";
import { GatheringPreviewSubPageProps } from "../types";

/**
 * Warning: The client may or may not be authenticated while in here
 */
const GatheringPreview = ({
	gathering,
	shouldDisplayUnlock,
}: {
	gathering: GatheringInitialEntryFragmentFragment;
	shouldDisplayUnlock?: boolean;
}) => {
	const { joinGathering, loading } = useJoinGathering({
		name: gathering.name,
		refCode: gathering.refCode,
		collectData: gathering.collectData,
	});
	const passwordRef = useRef<string>("");
	const textRef = useRef<TextInputHandler>(null);
	const [displayPassword, setDisplayPassword, displayPasswordRef] = useStateRef(false);

	const handleJoinGathering = useCallback(async () => {
		const enterPassword = displayPasswordRef.current;
		if (enterPassword && !passwordRef.current) {
			return;
		}
		const retGathering = await joinGathering({
			password: enterPassword ? passwordRef.current : undefined,
		});

		const handleValidation = (): { didJoin: boolean } => {
			if (retGathering?.privacy?.includes(GatheringPrivacy.PasswordRequired)) {
				if (hasValidGatheringAccess(retGathering)) {
					return {
						didJoin: true,
					};
				} else if (!enterPassword) {
					setDisplayPassword(true);
					return {
						didJoin: false,
					};
				} else if (
					enterPassword &&
					retGathering?.joinGatheringError === JoinGatheringError.IncorrectPassword
				) {
					toast({
						preset: "error",
						title: "Incorrect password",
						message: "Please try again",
					});
					return {
						didJoin: false,
					};
				} else if (
					enterPassword &&
					retGathering?.joinGatheringError === JoinGatheringError.TooManyPasswordAttempts
				) {
					toast({
						preset: "error",
						title: "Too many password attempts",
						message: "Please try again later",
					});
					return {
						didJoin: false,
					};
				} else {
					log.error(
						{
							gatheringId: gathering.gatheringId,
						},
						"Unknown error when joining gathering",
					);
					toast({
						preset: "error",
						title: "Unknown error",
						message: "Please try again later",
					});
					return {
						didJoin: false,
					};
				}
			}
			if (hasValidGatheringAccess(retGathering)) {
				return {
					didJoin: true,
				};
			}
			return {
				didJoin: false,
			};
		};
		handleValidation();
	}, [displayPasswordRef, joinGathering, setDisplayPassword, gathering.gatheringId]);
	const sharedProps: GatheringPreviewSubPageProps = useMemo(
		() => ({
			gathering,
			handleJoinGathering,
			joinLoading: loading,
		}),
		[gathering, handleJoinGathering, loading],
	);
	const { isPublicPreview, isFullPublic } = useMemo(() => {
		const isPublicPreview = gathering.privacy?.includes(GatheringPrivacy.PublicPreview);
		const isFullPublic = gathering.privacy?.includes(GatheringPrivacy.FullPublic);
		return { isPublicPreview, isFullPublic };
	}, [gathering.privacy]);
	useEffect(() => {
		track(amp.gathering.previewLoaded, {
			gatheringId: gathering.gatheringId,
			isPublicPreview,
		});
	}, [gathering.gatheringId, isPublicPreview]);
	const [overrideNoPublicPreview, setOverrideNoPublicPreview] = useState(false);

	if (isPublicPreview && !overrideNoPublicPreview) {
		return (
			<GatheringPreviewPublic
				{...sharedProps}
				overrideNoPublicPreview={() => {
					setOverrideNoPublicPreview(true);
				}}
			/>
		);
	}
	if (isFullPublic && !overrideNoPublicPreview && PUBLIC_ALBUM_VIEWABLE) {
		return (
			<GatheringPublicFull
				{...sharedProps}
				overrideNoPublicPreview={() => {
					setOverrideNoPublicPreview(true);
				}}
			/>
		);
	}

	return (
		<GatheringPreviewPrivate
			{...sharedProps}
			textRef={textRef}
			passwordRef={passwordRef}
			displayPassword={displayPassword}
			shouldDisplayUnlock={shouldDisplayUnlock}
		/>
	);
};
export default memo(GatheringPreview);
